<template>
    <div class="min-h-screen pt-12 -mx-4 bg-gray-300 sm:-mx-6">

        <ul class="absolute top-0 left-0 right-0 z-20 flex justify-center w-full mb-4 space-x-6 bg-white shadow-lg">
            <li
                @click="setView('unread')"
                class="px-4 py-2 mb-2 font-bold border-b-2 cursor-pointer select-none"
                :class="{ 'border-wlPrimary text-wlPrimary': viewmode === 'unread' }"
            >
                {{ _mft('notification:unread') }}
            </li>
            <li
                @click="setView('all')"
                class="px-4 py-2 mb-2 font-bold border-b-2 cursor-pointer select-none"
                :class="{ 'border-wlPrimary text-wlPrimary': viewmode === 'all' }"
            >
                {{ _mft('notification:all') }}
            </li>
            <li
                @click="showFilters = !showFilters"
                class="relative px-4 py-2 mb-2 font-bold cursor-pointer select-none"
                :class="[currentFilter !== null ? 'text-wlPrimary' : '']"
            >
                {{ _mft('notification:filter') }}
                <ul
                    class="absolute mb-4 overflow-hidden font-normal transition-all transform bg-white divide-y divide-gray-200 rounded-lg shadow-xl top-full -right-8"
                    :class="[showFilters ? '-translate-y-2 max-h-auto opacity-100' : '-translate-y-12 max-h-0 opacity-0']"
                >
                    <li
                        class="p-2 text-xs cursor-pointer"
                        @click="applyFilter(null)"
                        :class="[currentFilter === null ? 'bg-wlPrimary text-wlPrimaryContrast' : '']"
                    >
                        {{ _mft('notification:filter.showAll') }}
                    </li>
                    <li
                        v-for="type in translatedTypes"
                        :key="type.id"
                        @click="applyFilter(type)"
                        class="p-2 text-xs cursor-pointer"
                        :class="[currentFilter?.id === type.id ? 'bg-wlPrimary text-wlPrimaryContrast' : '']"
                    >
                        {{ type.name }}
                    </li>
                </ul>
            </li>
        </ul>

        <div class="px-4 pb-8 space-y-4">
            <div
                class="block py-3 text-center text-gray-400"
                v-if="!loadingDone"
            >
                <em>{{ _mft('notification:loadingNotifications') }}</em>
            </div>
            <div
                class="block py-3 text-center text-gray-400"
                v-else-if="!viewHasResults"
            >
                <em v-if="currentFilter !== null">{{ _mft('notification:filter.noResults', { filterName: currentFilter.name }) }}</em>
                <em v-else-if="viewmode === 'unread'">{{ _mft('notification:noUreadNotifications') }}</em>
                <em v-else>{{ _mft('notification:noNotifications') }}</em>
            </div>
            <list-notification
                v-for="notice in localNotifications"
                :key="notice.id"
                :notification="notice"
                :mode="viewmode"
                :filter="currentFilter"
                @toggleRead="toggleRead"
                @markReadAndNavigate="markReadAndNavigate"
            />

            <div
                v-if="loadingDone && viewPagination.last_page > 1"
                class="flex items-center justify-between px-3 py-2 bg-white rounded-lg shadow"
            >
                <button
                    class="text-sm font-semibold mf-link"
                    :disabled="!canGoPrevious"
                    :class="{ 'opacity-50 cursor-not-allowed': !canGoPrevious }"
                    @click="changePage(viewmode, viewPagination.current_page - 1)"
                >
                    Previous
                </button>
                <span class="text-sm text-gray-600">
                    Page {{ viewPagination.current_page }} / {{ viewPagination.last_page }}
                </span>
                <button
                    class="text-sm font-semibold mf-link"
                    :disabled="!canGoNext"
                    :class="{ 'opacity-50 cursor-not-allowed': !canGoNext }"
                    @click="changePage(viewmode, viewPagination.current_page + 1)"
                >
                    Next
                </button>
            </div>

        </div>
    </div>

</template>

<script>
    import axios from 'axios';

    const MAX_PER_PAGE = 25;

    const buildPaginationState = (perPage = MAX_PER_PAGE) => ({
        current_page: 1,
        last_page: 1,
        per_page: perPage,
        total: 0,
        has_more_pages: false,
        next_page: null,
        prev_page: null,
    });

    export default {
        props: {
            maxcount: {
                type: Number,
                default: MAX_PER_PAGE,
            },
        },
        data() {
            return {
                viewmode: 'unread',
                showFilters: false,
                currentFilter: null,
                loadingDone: false,
                notifications: {
                    types: [],
                    unreadCount: 0,
                    unread: [],
                    all: [],
                },
                pagination: {
                    unread: buildPaginationState(),
                    all: buildPaginationState(),
                },
            };
        },
        mounted() {
            this.resetPagination();
            this.fetchNotifications();
        },
        methods: {
            async fetchNotifications() {
                this.loadingDone = false;

                const params = {
                    limit: this.perPage,
                    page: this.pagination.all.current_page,
                    unread_page: this.pagination.unread.current_page,
                };

                if (this.currentFilter !== null) {
                    params.category_id = this.currentFilter.id;
                }

                try {
                    const response = await axios.get(route('notifications'), { params });

                    this.notifications.types = response.data.types;
                    this.notifications.unreadCount = response.data.unread_count;
                    this.notifications.unread = response.data.unread.data;
                    this.notifications.all = response.data.all.data;
                    this.pagination.unread = response.data.unread.meta;
                    this.pagination.all = response.data.all.meta;

                    this.$emit('update', this.notifications.unreadCount);
                } catch (error) {
                    console.error('Failed to load notifications', error);
                } finally {
                    this.loadingDone = true;
                }
            },
            async toggleRead(noteID) {
                await axios.post(route('notification.markAsRead', noteID));
                await this.refreshAfterMutation();
            },
            async markReadAndNavigate(notification) {
                if (!notification.read) {
                    await axios.post(route('notification.markAsRead', notification.id));
                }
                window.location.href = notification.url;
            },
            async markAllAsRead() {
                await axios.post(route('notification.markAllAsRead'));
                this.resetPagination();
                await this.fetchNotifications();
            },
            async changePage(mode, page) {
                const meta = this.pagination[mode];
                if (page < 1 || page > meta.last_page || page === meta.current_page) {
                    return;
                }

                this.pagination[mode] = {
                    ...meta,
                    current_page: page,
                };

                await this.fetchNotifications();
            },
            applyFilter(filter) {
                this.currentFilter = filter;
                this.showFilters = false;
            },
            setView(mode) {
                this.viewmode = mode;
            },
            resetPagination() {
                const paginationState = buildPaginationState(this.perPage);
                this.pagination.unread = { ...paginationState };
                this.pagination.all = { ...paginationState };
            },
            async refreshAfterMutation() {
                this.resetPagination();
                await this.fetchNotifications();
            },
        },
        computed: {
            perPage() {
                return Math.max(1, Math.min(this.maxcount, MAX_PER_PAGE));
            },
            translatedTypes() {
                return [...this.notifications.types]
                    .map((type) => ({
                        ...type,
                        name: _mft(`notification:category.${type.slug}`),
                    }))
                    .sort((a, b) => a.name.localeCompare(b.name));
            },
            localNotifications() {
                return this.notifications[this.viewmode] ?? [];
            },
            viewPagination() {
                return this.pagination[this.viewmode];
            },
            viewHasResults() {
                if (!this.loadingDone) {
                    return true;
                }

                return (this.viewPagination?.total ?? 0) > 0;
            },
            canGoPrevious() {
                return (this.viewPagination?.prev_page ?? null) !== null;
            },
            canGoNext() {
                return (this.viewPagination?.next_page ?? null) !== null;
            },
        },
        watch: {
            currentFilter() {
                this.resetPagination();
                this.fetchNotifications();
            },
        },
    };
</script>
<style>
.unread {
    color: #4e0075;
    font-weight: 800;
}
</style>
